/**
 * Costanti condivise per il modulo Agenda.
 *
 * Centralizza EVENT_COLORS, EVENT_TYPE_CONFIG, FILTER_CONFIG,
 * VIEW_LABELS e PARTICIPANT_CHIP_COLORS che erano duplicati in più file.
 */

import type { EventType } from '../types/agenda'
import { Users, Calendar, CheckCircle, Clock } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

// ---------------------------------------------------------------------------
// Colori evento — ora referenziano variabili CSS con fallback hardcodato
// ---------------------------------------------------------------------------

export const EVENT_COLORS: Record<string, string> = {
  meeting: 'var(--hexa-event-meeting, #8b5cf6)',
  evento: 'var(--hexa-event-evento, #00d3f2)',
  attivita: 'var(--hexa-event-attivita, #00d492)',
  task: 'var(--hexa-event-task, #fe9a00)',
  default: 'var(--hexa-event-default, #6b7280)',
}

/** Versioni a bassa opacità (25%) per sfondi pill trasparenti */
export const EVENT_COLORS_LIGHT: Record<string, string> = {
  meeting: 'var(--hexa-event-meeting-light)',
  evento: 'var(--hexa-event-evento-light)',
  attivita: 'var(--hexa-event-attivita-light)',
  task: 'var(--hexa-event-task-light)',
  default: 'var(--hexa-event-default-light)',
}

/** Colori testo titolo evento (tinta del tipo, adattata al tema) */
export const EVENT_TEXT_COLORS: Record<string, string> = {
  meeting: 'var(--hexa-event-meeting-text)',
  evento: 'var(--hexa-event-evento-text)',
  attivita: 'var(--hexa-event-attivita-text)',
  task: 'var(--hexa-event-task-text)',
  default: 'var(--hexa-event-default-text)',
}

/** Colori testo orario evento (più tenue del titolo) */
export const EVENT_TIME_COLORS: Record<string, string> = {
  meeting: 'var(--hexa-event-meeting-time)',
  evento: 'var(--hexa-event-evento-time)',
  attivita: 'var(--hexa-event-attivita-time)',
  task: 'var(--hexa-event-task-time)',
  default: 'var(--hexa-event-default-time)',
}

// ---------------------------------------------------------------------------
// Configurazione completa per tipo evento (label + colore + icona)
// ---------------------------------------------------------------------------

export interface EventTypeConfigEntry {
  label: string
  color: string
  icon: LucideIcon
}

export const EVENT_TYPE_CONFIG: Record<EventType, EventTypeConfigEntry> = {
  meeting: { label: 'Meeting', color: EVENT_COLORS.meeting, icon: Users },
  evento: { label: 'Evento', color: EVENT_COLORS.evento, icon: Calendar },
  attivita: { label: 'Attività', color: EVENT_COLORS.attivita, icon: CheckCircle },
  task: { label: 'Task', color: EVENT_COLORS.task, icon: Clock },
  scadenza: { label: 'Scadenza', color: EVENT_COLORS.default, icon: Calendar },
}

// ---------------------------------------------------------------------------
// Filtri toolbar (subset di EVENT_TYPE_CONFIG per uso nel dropdown)
// ---------------------------------------------------------------------------

export const FILTER_CONFIG = [
  { key: 'meeting' as const, label: 'Meeting', color: EVENT_COLORS.meeting },
  { key: 'evento' as const, label: 'Evento', color: EVENT_COLORS.evento },
  { key: 'attivita' as const, label: 'Attività', color: EVENT_COLORS.attivita },
  { key: 'task' as const, label: 'Task', color: EVENT_COLORS.task },
]

// ---------------------------------------------------------------------------
// Tipi evento per NewEventModal (task escluso — viene solo da modulo Task)
// ---------------------------------------------------------------------------

export const CREATABLE_EVENT_TYPES = [
  { id: 'meeting' as const, label: 'Meeting', icon: Users, color: EVENT_COLORS.meeting },
  { id: 'evento' as const, label: 'Evento', icon: Calendar, color: EVENT_COLORS.evento },
  { id: 'attivita' as const, label: 'Attività', icon: CheckCircle, color: EVENT_COLORS.attivita },
  // task non è creabile dall'agenda — viene dal modulo Task
]

// ---------------------------------------------------------------------------
// Label viste in italiano
// ---------------------------------------------------------------------------

export const VIEW_LABELS = {
  Month: 'Mese',
  Week: 'Settimana',
  Day: 'Giorno',
} as const

// ---------------------------------------------------------------------------
// Colori chip partecipanti
// ---------------------------------------------------------------------------

export const PARTICIPANT_CHIP_COLORS = {
  team: 'var(--hexa-chip-team, #7b3fff)',
  contact: 'var(--hexa-chip-contact, #00d492)',
} as const

// ---------------------------------------------------------------------------
// Layout viste Week/Day
// ---------------------------------------------------------------------------

/** Altezza in px di ogni slot orario nelle viste Week e Day */
export const HOUR_SLOT_HEIGHT = 45

/** Ora a cui scrollare inizialmente */
export const DEFAULT_SCROLL_HOUR = 7
